import { DecimalPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { PosCatalogProductDto } from '@klubovna/api-client';
import { TranslateModule } from '@ngx-translate/core';
import { FreestyleDrinkDialogData, FreestyleDrinkDialogResult } from '@posModels';
import { PosFreestyleDrinkApiService, FreestyleDrinkIngredient } from '@posServices';
import { NotificationService } from '@shared';
import { CENTS_PER_EUR, QTY_DEFAULT, QTY_MIN_PIECE, QTY_MIN_VOLUME, QTY_STEP_PIECE, QTY_STEP_VOLUME } from './freestyle-drink-dialog.constants';

@Component({
  selector: 'app-freestyle-drink-dialog',
  imports: [ DecimalPipe, FormsModule, MatDialogModule, TranslateModule ],
  templateUrl: './freestyle-drink-dialog.component.html',
  styleUrl: './freestyle-drink-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FreestyleDrinkDialogComponent {
  private dialogRef = inject(MatDialogRef<FreestyleDrinkDialogComponent>);
  private data = inject<FreestyleDrinkDialogData>(MAT_DIALOG_DATA);
  private freestyleDrinkApi = inject(PosFreestyleDrinkApiService);
  private notification = inject(NotificationService);
  private destroyRef = inject(DestroyRef);

  readonly availableProducts = this.data.availableProducts;

  selectedIngredients = signal<FreestyleDrinkIngredient[]>([]);
  drinkName = signal('');
  extraEur = signal(this.data.defaultExtraCents / CENTS_PER_EUR);
  searchQuery = signal('');
  submitting = signal(false);

  filteredProducts = computed(() => {
    const query = this.searchQuery().toLowerCase().trim();
    const selectedIds = new Set(this.selectedIngredients().map(i => i.product.id));
    return this.availableProducts.filter(p => {
      if (selectedIds.has(p.id)) return false;
      if (!query) return true;
      return p.name.toLowerCase().includes(query);
    });
  });

  calculatedPrice = computed(() => {
    const extraCents = Math.round(this.extraEur() * CENTS_PER_EUR);
    return extraCents + this.selectedIngredients().reduce((sum, i) => sum + i.qty * i.product.priceCents, 0);
  });

  canSubmit = computed(() => this.selectedIngredients().length > 0 && !this.submitting());

  addIngredient(product: PosCatalogProductDto): void {
    this.selectedIngredients.update(items => [ ...items, { product, qty: QTY_DEFAULT } ]);
  }

  removeIngredient(index: number): void {
    this.selectedIngredients.update(items => items.filter((_, i) => i !== index));
  }

  adjustQty(index: number, delta: number): void {
    this.selectedIngredients.update(items => {
      const updated = [ ...items ];
      const item = updated[index];
      const step = this.getStep(item.product);
      const minQty = this.getMinQty(item.product);
      const newQty = Math.max(minQty, Math.round((item.qty + delta * step) * 10) / 10);
      updated[index] = { ...item, qty: newQty };
      return updated;
    });
  }

  isAtMinQty(item: FreestyleDrinkIngredient): boolean {
    return item.qty <= this.getMinQty(item.product);
  }

  private getMinQty(product: PosCatalogProductDto): number {
    return product.volumeMl != null ? QTY_MIN_VOLUME : QTY_MIN_PIECE;
  }

  private getStep(product: PosCatalogProductDto): number {
    return product.volumeMl != null ? QTY_STEP_VOLUME : QTY_STEP_PIECE;
  }

  submit(): void {
    if (!this.canSubmit()) return;

    this.submitting.set(true);
    const extraCents = Math.round(this.extraEur() * CENTS_PER_EUR);
    const name = this.drinkName().trim() || this.generateDrinkName();

    this.freestyleDrinkApi
      .createFreestyleDrink(name, this.data.drinkCategoryId, extraCents, this.selectedIngredients())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (product) => {
          this.notification.success('pos.freestyle_drink.success');
          const result: FreestyleDrinkDialogResult = { success: true, createdProduct: product };
          this.dialogRef.close(result);
        },
        error: () => {
          this.notification.error('pos.freestyle_drink.error');
          this.submitting.set(false);
        },
      });
  }

  close(): void {
    this.dialogRef.close();
  }

  private generateDrinkName(): string {
    return this.selectedIngredients()
      .map(i => `${ i.qty }x ${ i.product.name.split(' ')[0] }`)
      .join(', ');
  }
}
